import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { BILLING } from '@/constants/testIds';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MagnifyingGlass, Plus, Trash, Printer, FloppyDisk, CurrencyInr, Users } from '@phosphor-icons/react';

export default function BillingPage() {
  const [view, setView] = useState('list'); // 'list', 'new', 'edit'
  const [editSale, setEditSale] = useState(null);
  const [sales, setSales] = useState([]);
  const [monthStats, setMonthStats] = useState({});
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(true);
  const [showAddParty, setShowAddParty] = useState(false);

  const loadSales = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.getSales({ month });
      setSales(data.sales || []);
      setMonthStats({ total: data.month_total || 0, orders: data.month_orders || 0, received: data.month_received || 0, due: data.month_due || 0 });
    } catch {}
    setLoading(false);
  }, [month]);

  useEffect(() => { loadSales(); }, [loadSales]);

  if (view === 'new') return <NewSaleForm onBack={() => { setView('list'); loadSales(); }} />;
  if (view === 'edit' && editSale) return <NewSaleForm onBack={() => { setView('list'); setEditSale(null); loadSales(); }} editSale={editSale} />;

  const handleInvoiceClick = async (sale) => {
    const pwd = window.prompt('Enter edit password to modify this invoice:');
    if (!pwd) return;
    if (pwd !== '9255227867') { alert('Invalid password. Access denied.'); return; }
    try {
      const { data } = await api.getSale(sale.id);
      setEditSale(data);
      setView('edit');
    } catch { alert('Failed to load sale'); }
  };

  return (
    <div data-testid={BILLING.container} className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>Billing</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAddParty(true)} variant="outline" className="h-9 px-4 text-sm font-semibold rounded-md" style={{ borderColor: '#E2E4E9' }}>
            <Users size={16} className="mr-1" /> Add Party
          </Button>
          <Button
            data-testid={BILLING.newSaleBtn}
            onClick={() => setView('new')}
            className="h-9 px-4 text-sm font-semibold rounded-md"
            style={{ background: '#0A2540', color: 'white' }}
          >
            <Plus size={16} weight="bold" className="mr-1" /> New Sale
          </Button>
        </div>
      </div>

      <Tabs defaultValue="sales">
        <TabsList>
          <TabsTrigger value="sales">Sale Book</TabsTrigger>
          <TabsTrigger value="parties">Parties</TabsTrigger>
        </TabsList>

        <TabsContent value="sales" className="space-y-4 mt-4">
          {/* Month Selector + Monthly Total */}
          <div className="flex items-end gap-4">
            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Month</Label>
              <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="h-8 mt-1 w-48" />
            </div>
            <div className="flex gap-3">
              <div className="px-4 py-2 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
                <p className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Month Total</p>
                <p className="text-lg font-extrabold" style={{ color: '#0A2540', fontVariantNumeric: 'tabular-nums' }}>₹{(monthStats.total || 0).toLocaleString()}</p>
              </div>
              <div className="px-4 py-2 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
                <p className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Orders</p>
                <p className="text-lg font-extrabold" style={{ color: '#007AFF', fontVariantNumeric: 'tabular-nums' }}>{monthStats.orders || 0}</p>
              </div>
              <div className="px-4 py-2 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
                <p className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Received</p>
                <p className="text-lg font-extrabold" style={{ color: '#34C759', fontVariantNumeric: 'tabular-nums' }}>₹{(monthStats.received || 0).toLocaleString()}</p>
              </div>
              <div className="px-4 py-2 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
                <p className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Due</p>
                <p className="text-lg font-extrabold" style={{ color: '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>₹{(monthStats.due || 0).toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Sales Table */}
          <div data-testid={BILLING.salesList} className="rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
            <table className="w-full text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              <thead>
            <tr style={{ background: '#F9F9FA', borderBottom: '1px solid #E2E4E9' }}>
              <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Invoice</th>
              <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Date</th>
              <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Customer</th>
              <th className="text-right px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Items</th>
              <th className="text-right px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Net Payable</th>
              <th className="text-right px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Status</th>
              <th className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>Loading...</td></tr>
            ) : sales.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>No sales recorded yet</td></tr>
            ) : sales.map((sale, i) => (
              <tr key={sale.id || i} className="hover:bg-slate-50 transition-colors" style={{ borderBottom: '1px solid #E2E4E9' }}>
                <td className="px-4 py-2.5">
                  <button onClick={() => handleInvoiceClick(sale)} className="font-medium hover:underline" style={{ color: '#007AFF' }}>{sale.invoice_number}</button>
                </td>
                <td className="px-4 py-2.5" style={{ color: '#8A94A6' }}>{sale.bill_date}</td>
                <td className="px-4 py-2.5" style={{ color: '#0A2540' }}>{sale.customer_name}</td>
                <td className="px-4 py-2.5 text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>{sale.total_items}</td>
                <td className="px-4 py-2.5 text-right font-medium" style={{ color: '#0A2540', fontVariantNumeric: 'tabular-nums' }}>₹{(sale.net_payable || 0).toLocaleString()}</td>
                <td className="px-4 py-2.5 text-right">
                  <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase" style={{
                    background: sale.balance_due > 0 ? '#FFF0F0' : '#F0FFF4',
                    color: sale.balance_due > 0 ? '#FF3B30' : '#34C759'
                  }}>
                    {sale.balance_due > 0 ? 'Due' : 'Paid'}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <a
                    href={api.getInvoicePdf(sale.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium hover:underline"
                    style={{ color: '#007AFF' }}
                  >
                    <Printer size={14} /> PDF
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        </TabsContent>

        <TabsContent value="parties" className="mt-4">
          <PartiesList />
        </TabsContent>
      </Tabs>

      {/* Add Party Dialog */}
      <Dialog open={showAddParty} onOpenChange={() => setShowAddParty(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Add New Party</DialogTitle></DialogHeader>
          <AddPartyForm onSave={() => setShowAddParty(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PartiesList() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getCustomers(search).then(r => { setCustomers(r.data); setLoading(false); });
  }, [search]);

  return (
    <div className="space-y-4">
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#8A94A6' }} />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search parties by name or phone..." className="pl-9 h-9" />
      </div>
      <div className="rounded-md border overflow-x-auto" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <table className="w-full text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <thead><tr style={{ background: '#F9F9FA', borderBottom: '1px solid #E2E4E9' }}>
            {['Name', 'Phone', 'Area', 'Type', 'Total Purchases', 'Outstanding'].map(h => (
              <th key={h} className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>Loading...</td></tr> :
              customers.map(c => (
                <tr key={c.id} className="hover:bg-slate-50 transition-colors" style={{ borderBottom: '1px solid #E2E4E9' }}>
                  <td className="px-4 py-2 font-medium" style={{ color: '#0A2540' }}>{c.name}</td>
                  <td className="px-4 py-2 text-xs" style={{ color: '#8A94A6' }}>{c.phone}</td>
                  <td className="px-4 py-2 text-xs">{c.area}</td>
                  <td className="px-4 py-2"><span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: '#F0F4F8', color: '#0A2540' }}>{c.customer_type}</span></td>
                  <td className="px-4 py-2 text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{(c.total_purchases || 0).toLocaleString()}</td>
                  <td className="px-4 py-2 text-right" style={{ fontVariantNumeric: 'tabular-nums', color: c.outstanding_balance > 0 ? '#FF3B30' : '#34C759' }}>₹{(c.outstanding_balance || 0).toLocaleString()}</td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AddPartyForm({ onSave }) {
  const [form, setForm] = useState({ name: '', contact_person: '', phone: '', email: '', gst_number: '', dl_number: '', address: '', area: '', customer_type: 'Medical' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setError('');
    if (!form.gst_number) { setError('GST Number is mandatory'); return; }
    if (form.gst_number.length !== 15) { setError('GST Number must be 15 characters'); return; }
    if (!form.dl_number) { setError('Drug License Number is mandatory'); return; }
    setSaving(true);
    try { await api.createCustomer(form); onSave(); } catch (e) { setError(e.response?.data?.detail || 'Error creating party'); }
    setSaving(false);
  };
  return (
    <div className="space-y-3">
      {error && <div className="p-2 rounded text-xs font-medium" style={{ background: '#FFF0F0', color: '#FF3B30' }}>{error}</div>}
      {[
        { k: 'name', l: 'Party Name *' }, { k: 'contact_person', l: 'Contact Person' },
        { k: 'phone', l: 'Mobile Number *' }, { k: 'gst_number', l: 'GST Number * (15 chars)' },
        { k: 'dl_number', l: 'Drug License Number *' }, { k: 'address', l: 'Address' }, { k: 'area', l: 'Area' },
      ].map(f => (
        <div key={f.k}><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{f.l}</Label>
          <Input value={form[f.k]} onChange={e => setForm(fo => ({ ...fo, [f.k]: e.target.value }))} className="h-8 mt-1 text-sm"
            placeholder={f.k === 'gst_number' ? 'e.g. 24AABCP1234N1ZR' : f.k === 'dl_number' ? 'e.g. 20C/VAD/94560' : ''} /></div>
      ))}
      <div><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Type</Label>
        <Select value={form.customer_type} onValueChange={v => setForm(f => ({ ...f, customer_type: v }))}>
          <SelectTrigger className="h-8 mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Medical">Medical</SelectItem>
            <SelectItem value="General">General</SelectItem>
            <SelectItem value="Hospital">Hospital</SelectItem>
          </SelectContent>
        </Select></div>
      <Button onClick={save} disabled={saving} className="w-full h-9 text-sm font-semibold" style={{ background: '#0A2540', color: 'white' }}>
        {saving ? 'Saving...' : 'Create Party'}
      </Button>
    </div>
  );
}

function NewSaleForm({ onBack, editSale }) {
  const isEdit = !!editSale;
  const [billDate, setBillDate] = useState(editSale?.bill_date || new Date().toISOString().split('T')[0]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(editSale ? { id: editSale.customer_id, name: editSale.customer_name } : null);
  const [doctorSearch, setDoctorSearch] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(editSale ? { id: editSale.doctor_id, name: editSale.doctor_name } : null);
  const [medSearch, setMedSearch] = useState('');
  const [medicines, setMedicines] = useState([]);
  const [items, setItems] = useState(editSale?.items || []);
  const [paymentMode, setPaymentMode] = useState('Credit');
  const [amountReceived, setAmountReceived] = useState(editSale?.amount_received?.toString() || '');
  const [saving, setSaving] = useState(false);
  const [showCustDropdown, setShowCustDropdown] = useState(false);
  const [showDocDropdown, setShowDocDropdown] = useState(false);
  const [showMedDropdown, setShowMedDropdown] = useState(false);

  // Search customers
  useEffect(() => {
    if (customerSearch.length >= 2) {
      api.getCustomers(customerSearch).then(r => { setCustomers(r.data); setShowCustDropdown(true); });
    } else { setCustomers([]); setShowCustDropdown(false); }
  }, [customerSearch]);

  // Search doctors
  useEffect(() => {
    if (doctorSearch.length >= 2) {
      api.getDoctors(doctorSearch).then(r => { setDoctors(r.data); setShowDocDropdown(true); });
    } else { setDoctors([]); setShowDocDropdown(false); }
  }, [doctorSearch]);

  // Search medicines
  useEffect(() => {
    if (medSearch.length >= 1) {
      api.getMedicines(medSearch).then(r => { setMedicines(r.data); setShowMedDropdown(true); });
    } else { setMedicines([]); setShowMedDropdown(false); }
  }, [medSearch]);

  const addItem = (med) => {
    const exists = items.find(i => i.medicine_id === med.id);
    if (exists) {
      setItems(items.map(i => i.medicine_id === med.id ? { ...i, qty: i.qty + 1, amount: (i.qty + 1) * i.mrp * (1 - i.discount_percent / 100) } : i));
    } else {
      setItems([...items, {
        medicine_id: med.id, medicine_name: med.name, batch_number: med.batch_number || '',
        expiry_date: med.expiry_date || '', unit: med.unit || 'Strip', mrp: med.mrp,
        qty: 1, discount_percent: 0, dealer_price: med.dealer_price, gst_percent: med.gst_percent,
        amount: med.mrp
      }]);
    }
    setMedSearch('');
    setShowMedDropdown(false);
  };

  const updateItem = (idx, field, value) => {
    setItems(items.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      if (['qty', 'mrp', 'discount_percent'].includes(field)) {
        updated.amount = Number(updated.qty) * Number(updated.mrp) * (1 - Number(updated.discount_percent) / 100);
      }
      return updated;
    }));
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  // Calculations
  const grossAmount = items.reduce((s, i) => s + i.amount, 0);
  const totalDiscount = items.reduce((s, i) => s + (i.qty * i.mrp * i.discount_percent / 100), 0);
  const totalGst = items.reduce((s, i) => s + (i.amount * i.gst_percent / 100), 0);
  const netPayable = Math.round(grossAmount + totalGst);
  const roundOff = netPayable - (grossAmount + totalGst);
  const totalQty = items.reduce((s, i) => s + Number(i.qty), 0);
  const received = Number(amountReceived) || 0;
  const balanceDue = Math.max(0, netPayable - received);

  const saveSale = async (print = false) => {
    if (items.length === 0) return;
    if (!selectedCustomer) { alert('Please select an existing customer. Creating new customers during billing is not allowed.'); return; }
    if (!selectedDoctor) { alert('Doctor name is mandatory. Please select a doctor.'); return; }
    setSaving(true);
    try {
      const saleData = {
        bill_date: billDate,
        customer_id: selectedCustomer?.id || null,
        customer_name: selectedCustomer?.name || 'Walk-in',
        doctor_id: selectedDoctor?.id || null,
        doctor_name: selectedDoctor?.name || '',
        salesperson: 'Admin',
        payment_mode: paymentMode,
        delivery_method: 'Pickup',
        items,
        total_qty: totalQty,
        total_items: items.length,
        gross_amount: grossAmount,
        total_discount: totalDiscount,
        total_gst: Math.round(totalGst * 100) / 100,
        round_off: Math.round(roundOff * 100) / 100,
        net_payable: netPayable,
        amount_received: received,
        balance_due: balanceDue,
      };
      if (isEdit) {
        saleData.edit_password = '9255227867';
        await api.updateSale(editSale.id, saleData);
      } else {
        const { data } = await api.createSale(saleData);
        if (print && data.id) {
          window.open(api.getInvoicePdf(data.id), '_blank');
        }
      }
      onBack();
    } catch (err) {
      console.error('Save failed:', err);
    }
    setSaving(false);
  };

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-sm font-medium hover:underline" style={{ color: '#007AFF' }}>
            ← Sale Book
          </button>
          <span className="text-sm" style={{ color: '#8A94A6' }}>/</span>
          <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>{isEdit ? 'Edit Sale' : 'New Sale'}</h1>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid={BILLING.saveSaleBtn}
            onClick={() => saveSale(false)}
            disabled={saving || items.length === 0}
            className="h-9 px-4 text-sm font-semibold rounded-md"
            style={{ background: '#0A2540', color: 'white' }}
          >
            <FloppyDisk size={16} className="mr-1" /> Save
          </Button>
          <Button
            data-testid={BILLING.savePrintBtn}
            onClick={() => saveSale(true)}
            disabled={saving || items.length === 0}
            className="h-9 px-4 text-sm font-semibold rounded-md"
            style={{ background: '#007AFF', color: 'white' }}
          >
            <Printer size={16} className="mr-1" /> Save & Print
          </Button>
        </div>
      </div>

      {/* Top Row: Date, Customer, Doctor, Payment */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Bill Date</Label>
          <Input data-testid={BILLING.billDate} type="date" value={billDate} onChange={(e) => setBillDate(e.target.value)} className="h-9 mt-1" />
        </div>
        <div className="relative">
          <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Customer</Label>
          <Input
            data-testid={BILLING.customerSearch}
            value={selectedCustomer ? selectedCustomer.name : customerSearch}
            onChange={(e) => { setCustomerSearch(e.target.value); setSelectedCustomer(null); }}
            placeholder="Search by name/phone"
            className="h-9 mt-1"
          />
          {showCustDropdown && customers.length > 0 && (
            <div className="absolute z-20 mt-1 w-full border rounded-md shadow-lg max-h-48 overflow-y-auto" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
              {customers.map(c => (
                <button key={c.id} onClick={() => { setSelectedCustomer(c); setShowCustDropdown(false); setCustomerSearch(''); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors" style={{ borderBottom: '1px solid #E2E4E9' }}>
                  {c.name} <span style={{ color: '#8A94A6' }}>({c.phone})</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="relative">
          <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Doctor</Label>
          <Input
            data-testid={BILLING.doctorSearch}
            value={selectedDoctor ? selectedDoctor.name : doctorSearch}
            onChange={(e) => { setDoctorSearch(e.target.value); setSelectedDoctor(null); }}
            placeholder="Search doctor"
            className="h-9 mt-1"
          />
          {showDocDropdown && doctors.length > 0 && (
            <div className="absolute z-20 mt-1 w-full border rounded-md shadow-lg max-h-48 overflow-y-auto" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
              {doctors.map(d => (
                <button key={d.id} onClick={() => { setSelectedDoctor(d); setShowDocDropdown(false); setDoctorSearch(''); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors" style={{ borderBottom: '1px solid #E2E4E9' }}>
                  {d.name} <span style={{ color: '#8A94A6' }}>({d.specialization})</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Payment Mode</Label>
          <Input data-testid={BILLING.paymentMode} value="Credit" disabled className="h-9 mt-1 bg-slate-50" />
        </div>
      </div>

      {/* Doctor mandatory indicator */}
      {!selectedDoctor && (
        <div className="p-2 rounded-md text-xs font-medium" style={{ background: '#FFF0F0', color: '#FF3B30', border: '1px solid #FFD4D4' }}>
          Doctor name is mandatory before saving the bill
        </div>
      )}
      {!selectedCustomer && (
        <div className="p-2 rounded-md text-xs font-medium" style={{ background: '#FFF8E1', color: '#E6A800', border: '1px solid #FFE082' }}>
          Select an existing customer (new customer creation not allowed during billing)
        </div>
      )}

      {/* Medicine Search */}
      <div className="relative">
        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#8A94A6' }} />
          <Input
            data-testid={BILLING.medicineSearch}
            value={medSearch}
            onChange={(e) => setMedSearch(e.target.value)}
            placeholder="Search medicine by name, barcode, generic name, SKU..."
            className="pl-10 h-10"
            style={{ borderColor: '#E2E4E9' }}
          />
        </div>
        {showMedDropdown && medicines.length > 0 && (
          <div className="absolute z-20 mt-1 w-full border rounded-md shadow-lg max-h-64 overflow-y-auto" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
            {medicines.map(m => (
              <button key={m.id} onClick={() => addItem(m)}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors flex justify-between items-center" style={{ borderBottom: '1px solid #E2E4E9' }}>
                <div>
                  <span className="font-medium" style={{ color: '#0A2540' }}>{m.name}</span>
                  <span className="ml-2 text-xs" style={{ color: '#8A94A6' }}>{m.generic_name} | {m.manufacturer}</span>
                </div>
                <div className="text-right">
                  <span className="text-xs font-medium" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{m.mrp}</span>
                  <span className="ml-2 text-[10px]" style={{ color: m.stock > 50 ? '#34C759' : '#FF3B30' }}>Stock: {m.stock}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Product Grid */}
      <div data-testid={BILLING.productGrid} className="rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <table className="w-full text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <thead>
            <tr style={{ background: '#0A2540' }}>
              {['#', 'Medicine', 'Batch', 'Expiry', 'Unit', 'MRP', 'Qty', 'Disc%', 'GST%', 'Amount', ''].map(h => (
                <th key={h} className="px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-left text-white">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-sm" style={{ color: '#8A94A6' }}>Search and add medicines above</td></tr>
            ) : items.map((item, idx) => (
              <tr key={idx} className="hover:bg-slate-50 transition-colors" style={{ borderBottom: '1px solid #E2E4E9' }}>
                <td className="px-3 py-1.5 text-xs" style={{ color: '#8A94A6' }}>{idx + 1}</td>
                <td className="px-3 py-1.5 font-medium text-xs" style={{ color: '#0A2540' }}>{item.medicine_name}</td>
                <td className="px-3 py-1.5 text-xs" style={{ color: '#8A94A6' }}>{item.batch_number}</td>
                <td className="px-3 py-1.5 text-xs" style={{ color: '#8A94A6' }}>{item.expiry_date}</td>
                <td className="px-3 py-1.5 text-xs">{item.unit}</td>
                <td className="px-3 py-1.5 text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  <Input type="number" value={item.mrp} onChange={(e) => updateItem(idx, 'mrp', Number(e.target.value))} className="h-7 w-16 text-xs text-right" />
                </td>
                <td className="px-3 py-1.5">
                  <Input type="number" value={item.qty} onChange={(e) => updateItem(idx, 'qty', Number(e.target.value))} className="h-7 w-14 text-xs text-right" min="1" />
                </td>
                <td className="px-3 py-1.5">
                  <Input type="number" value={item.discount_percent} onChange={(e) => updateItem(idx, 'discount_percent', Number(e.target.value))} className="h-7 w-14 text-xs text-right" />
                </td>
                <td className="px-3 py-1.5 text-xs text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>{item.gst_percent}%</td>
                <td className="px-3 py-1.5 text-right font-medium" style={{ fontVariantNumeric: 'tabular-nums', color: '#0A2540' }}>₹{item.amount.toFixed(2)}</td>
                <td className="px-3 py-1.5">
                  <button onClick={() => removeItem(idx)} className="p-1 rounded hover:bg-red-50 transition-colors"><Trash size={14} style={{ color: '#FF3B30' }} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bottom Summary */}
      <div className="flex justify-between items-start gap-8">
        <div className="flex-1 p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Amount Received</Label>
          <Input type="number" value={amountReceived} onChange={(e) => setAmountReceived(e.target.value)} placeholder="0" className="h-9 mt-1 w-48" />
        </div>
        <div data-testid={BILLING.saleTotal} className="w-72 p-4 rounded-md border space-y-1" style={{ background: '#FFFFFF', borderColor: '#E2E4E9', fontVariantNumeric: 'tabular-nums' }}>
          <div className="flex justify-between text-sm"><span style={{ color: '#8A94A6' }}>Total Qty</span><span>{totalQty}</span></div>
          <div className="flex justify-between text-sm"><span style={{ color: '#8A94A6' }}>Gross</span><span>₹{grossAmount.toFixed(2)}</span></div>
          <div className="flex justify-between text-sm"><span style={{ color: '#8A94A6' }}>Discount</span><span style={{ color: '#FF3B30' }}>-₹{totalDiscount.toFixed(2)}</span></div>
          <div className="flex justify-between text-sm"><span style={{ color: '#8A94A6' }}>GST</span><span>₹{totalGst.toFixed(2)}</span></div>
          <div className="flex justify-between text-sm"><span style={{ color: '#8A94A6' }}>Round Off</span><span>₹{roundOff.toFixed(2)}</span></div>
          <div className="border-t pt-1 mt-1" style={{ borderColor: '#E2E4E9' }}>
            <div className="flex justify-between text-base font-bold" style={{ color: '#0A2540' }}><span>Net Payable</span><span>₹{netPayable}</span></div>
          </div>
          {balanceDue > 0 && (
            <div className="flex justify-between text-sm font-medium" style={{ color: '#FF3B30' }}><span>Balance Due</span><span>₹{balanceDue}</span></div>
          )}
        </div>
      </div>
    </div>
  );
}
